import React, { useState, useEffect } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, signInWithPopup, GoogleAuthProvider, signOut, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, getDoc, collection, getDocs, orderBy, limit, query } from 'firebase/firestore';
import { 
  Menu, 
  X, 
  LogOut, 
  User, 
  FileText, 
  Image, 
  HelpCircle, 
  MessageCircle, 
  Users,
  Shield,
  Eye,
  Calendar,
  BarChart2,
  TrendingUp,
  Activity
} from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  BarElement,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import './App.css';

// Import components
import AddLogos from './AddLogo';
import AddBlogs from './AdminAddBlog';
import AddClaimForms from './AddClaimForm';
import ViewBlogs from './ViewBlogs';
import ViewSubscribers from './ViewSubscribers';
import ViewAppointments from './ViewAppointments';
import ViewVisitors from './ViewVisitors';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  BarElement
);

// Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyCRJbaL5y1IkO-MvSLfl56eAIbCdJ6uQy8",
  authDomain: "samriddhi-health.firebaseapp.com",
  projectId: "samriddhi-health",
  storageBucket: "samriddhi-health.firebasestorage.app",
  messagingSenderId: "850622578053",
  appId: "1:850622578053:web:721879139660de4bd858ec",
  measurementId: "G-4BDJ4WDBC9"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

// Admin emails
const ADMIN_EMAILS = [
  'kallesh953@gmail.com',
  'kallesh@gmail.com',
  'arun.d@samhealth.co.in',
  'arun@samhealth.co.in'
];

// Components for different admin sections
const ViewContacts = () => (
  <div className="p-6">
    <h2 className="text-2xl font-bold mb-4">View Contacts</h2>
    <div className="bg-white rounded-lg shadow p-6">
      <p>View and manage contact submissions here.</p>
      {/* Add your contacts viewing functionality here */}
    </div>
  </div>
);

const Dashboard = () => {
  const [totalVisitors, setTotalVisitors] = useState(null);
  const [dailyCounts, setDailyCounts] = useState([]);
  const [chartData, setChartData] = useState(null);
  const [weeklyGrowth, setWeeklyGrowth] = useState(0);

  useEffect(() => {
    const db = getFirestore();
    // Fetch total unique visitors
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'visitorTotals', 'summary'));
        setTotalVisitors(snap.exists() ? (snap.data().totalUnique || 0) : 0);
      } catch (e) {
        setTotalVisitors(0);
      }
    })();
    // Fetch last 30 daily counts for better chart
    (async () => {
      try {
        const q = query(collection(db, 'visitorDaily'), orderBy('date', 'desc'), limit(30));
        const qs = await getDocs(q);
        const rows = [];
        qs.forEach(d => rows.push({ date: d.id || d.data().date, count: (d.data().count || 0) }));
        rows.reverse();
        setDailyCounts(rows.map(r => r.count));
        
        // Calculate weekly growth
        const lastWeek = rows.slice(-7).reduce((sum, r) => sum + r.count, 0);
        const prevWeek = rows.slice(-14, -7).reduce((sum, r) => sum + r.count, 0);
        const growth = prevWeek > 0 ? ((lastWeek - prevWeek) / prevWeek * 100) : 0;
        setWeeklyGrowth(growth);

        // Prepare chart data
        setChartData({
          labels: rows.map(r => {
            const date = new Date(r.date);
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          }),
          datasets: [
            {
              label: 'Daily Unique Visitors',
              data: rows.map(r => r.count),
              borderColor: 'rgb(16, 185, 129)',
              backgroundColor: 'rgba(16, 185, 129, 0.1)',
              tension: 0.4,
              fill: true,
              pointBackgroundColor: 'rgb(16, 185, 129)',
              pointBorderColor: 'white',
              pointBorderWidth: 2,
              pointRadius: 4,
            }
          ]
        });
      } catch (e) {
        setDailyCounts([]);
      }
    })();
  }, []);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        cornerRadius: 8,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#6B7280',
          font: {
            size: 12,
          },
        },
      },
      y: {
        grid: {
          color: 'rgba(229, 231, 235, 0.5)',
        },
        ticks: {
          color: '#6B7280',
          font: {
            size: 12,
          },
        },
      },
    },
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-bold text-gray-900">Dashboard</h2>
        <div className="text-sm text-gray-500">Last updated: {new Date().toLocaleTimeString()}</div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Visitors Card */}
        <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-emerald-100 text-sm font-medium">Total Visitors</p>
              <p className="text-3xl font-bold">{totalVisitors ?? '—'}</p>
            </div>
            <div className="bg-white/20 rounded-lg p-3">
              <Users className="w-6 h-6" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-emerald-100 text-sm">
            <TrendingUp className="w-4 h-4 mr-1" />
            <span>{weeklyGrowth > 0 ? '+' : ''}{weeklyGrowth.toFixed(1)}% this week</span>
          </div>
        </div>

        {/* Today's Visitors */}
        <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Today's Visitors</p>
              <p className="text-3xl font-bold text-gray-900">{dailyCounts[dailyCounts.length - 1] || 0}</p>
            </div>
            <div className="bg-blue-100 rounded-lg p-3">
              <Activity className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        {/* Average Daily */}
        <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Daily Average</p>
              <p className="text-3xl font-bold text-gray-900">
                {dailyCounts.length > 0 ? Math.round(dailyCounts.reduce((a,b) => a+b, 0) / dailyCounts.length) : 0}
              </p>
            </div>
            <div className="bg-purple-100 rounded-lg p-3">
              <BarChart2 className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>

        {/* Peak Day */}
        <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm font-medium">Peak Day</p>
              <p className="text-3xl font-bold text-gray-900">{Math.max(...dailyCounts) || 0}</p>
            </div>
            <div className="bg-orange-100 rounded-lg p-3">
              <TrendingUp className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Visitor Analytics Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-xl shadow-lg border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-gray-900">Visitor Analytics</h3>
            <div className="flex items-center space-x-2 text-sm text-gray-500">
              <div className="w-3 h-3 bg-emerald-500 rounded-full"></div>
              <span>Daily Unique Visitors</span>
            </div>
          </div>
          <div className="h-80">
            {chartData ? (
              <Line data={chartData} options={chartOptions} />
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Quick Actions</h3>
          <div className="space-y-4">
            <div className="bg-blue-50 rounded-lg p-4 hover:bg-blue-100 transition-colors cursor-pointer">
              <div className="flex items-center">
                <Image className="w-5 h-5 text-blue-600 mr-3" />
                <span className="font-medium text-blue-900">Manage Logos</span>
              </div>
            </div>
            <div className="bg-green-50 rounded-lg p-4 hover:bg-green-100 transition-colors cursor-pointer">
              <div className="flex items-center">
                <FileText className="w-5 h-5 text-green-600 mr-3" />
                <span className="font-medium text-green-900">Add Blog Post</span>
              </div>
            </div>
            <div className="bg-purple-50 rounded-lg p-4 hover:bg-purple-100 transition-colors cursor-pointer">
              <div className="flex items-center">
                <HelpCircle className="w-5 h-5 text-purple-600 mr-3" />
                <span className="font-medium text-purple-900">Claim Forms</span>
              </div>
            </div>
            <div className="bg-orange-50 rounded-lg p-4 hover:bg-orange-100 transition-colors cursor-pointer">
              <div className="flex items-center">
                <MessageCircle className="w-5 h-5 text-orange-600 mr-3" />
                <span className="font-medium text-orange-900">View Messages</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('dashboard');

  const sidebarItems = [
    { id: 'dashboard', label: 'Dashboard', icon: Shield },
    { id: 'add-logos', label: 'Add Logos', icon: Image },
    { id: 'add-blogs', label: 'Add Blogs', icon: FileText },
    { id: 'view-blogs', label: 'View Blogs', icon: Eye },
    { id: 'add-claim-forms', label: 'Add Claim Forms', icon: HelpCircle },
    { id: 'view-appointments', label: 'View Appointments', icon: Calendar },
    { id: 'view-contacts', label: 'View Contacts', icon: MessageCircle },
  { id: 'view-subscribers', label: 'View Subscribers', icon: Users },
  { id: 'view-visitors', label: 'View Visitors', icon: BarChart2 }
  ];

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const signInWithGoogle = async () => {
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const userEmail = result.user.email;
      
      if (!ADMIN_EMAILS.includes(userEmail)) {
        await signOut(auth);
        alert('Access denied. You are not authorized to access this admin panel.');
        return;
      }
    } catch (error) {
      console.error('Error signing in:', error);
      alert('Error signing in. Please try again.');
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'add-logos':
        return <AddLogos />;
      case 'add-blogs':
        return <AddBlogs />;
      case 'view-blogs':
        return <ViewBlogs />;
      case 'add-claim-forms':
        return <AddClaimForms />;
      case 'view-appointments':
        return <ViewAppointments />;
      case 'view-contacts':
        return <ViewContacts />;
      case 'view-subscribers':
        return <ViewSubscribers />;
      case 'view-visitors':
        return <ViewVisitors />;
      default:
        return <Dashboard />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!user || !ADMIN_EMAILS.includes(user.email)) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full mx-4">
          <div className="text-center mb-8">
            <Shield className="w-16 h-16 text-blue-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Admin Access</h1>
            <p className="text-gray-600">Sign in with your authorized Google account</p>
          </div>
          
          <button
            onClick={signInWithGoogle}
            className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 px-4 rounded-lg transition duration-200 flex items-center justify-center"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign in with Google
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Sidebar */}
      <div className={`bg-white shadow-lg transition-transform duration-300 ease-in-out ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } fixed inset-y-0 left-0 z-50 w-64 md:relative md:translate-x-0`}>
        
        {/* Sidebar Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-bold text-gray-800">Admin Panel</h2>
          <button
            onClick={() => setSidebarOpen(false)}
            className="md:hidden p-2 rounded-lg hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* User Info */}
        <div className="p-4 border-b">
          <div className="flex items-center">
            <img
              src={user.photoURL}
              alt="Profile"
              className="w-10 h-10 rounded-full mr-3"
            />
            <div>
              <p className="font-semibold text-sm text-gray-800">{user.displayName}</p>
              <p className="text-xs text-gray-500">{user.email}</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="p-4">
          {sidebarItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => {
                  setActiveSection(item.id);
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center px-4 py-3 mb-2 rounded-lg transition-colors ${
                  activeSection === item.id
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <Icon className="w-5 h-5 mr-3" />
                {item.label}
              </button>
            );
          })}
        </nav>

        {/* Sign Out */}
        <div className="absolute bottom-4 left-4 right-4">
          <button
            onClick={handleSignOut}
            className="w-full flex items-center px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            <LogOut className="w-5 h-5 mr-3" />
            Sign Out
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Top Header */}
        <header className="bg-white shadow-sm border-b p-4 flex items-center justify-between">
          <div className="flex items-center">
            <button
              onClick={() => setSidebarOpen(true)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 mr-4"
            >
              <Menu className="w-5 h-5" />
            </button>
            <h1 className="text-xl font-semibold text-gray-800">
              {sidebarItems.find(item => item.id === activeSection)?.label || 'Dashboard'}
            </h1>
          </div>
          
          <div className="flex items-center">
            <span className="text-sm text-gray-600 mr-4">Welcome, {user.displayName}</span>
            <img
              src={user.photoURL}
              alt="Profile"
              className="w-8 h-8 rounded-full"
            />
          </div>
        </header>

        {/* Content Area */}
        <main className="flex-1 bg-gray-50">
          {renderContent()}
        </main>
      </div>

      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
}

export default App;
       